import { useEffect, useRef, useState } from 'react';
import { AlertCircleIcon, MapPinIcon } from 'lucide-react';
import { Marker as MaplibreMarker, type Map as MaplibreMap } from 'maplibre-gl';
import 'maplibre-gl/dist/maplibre-gl.css';
import { createOsmMap, resizeOsmMap } from './base-map';
import { usePhotoMarkers, type PhotoLocation } from './use-photo-markers';

interface ReportLocationMapProps {
  latitude: number;
  longitude: number;
  /** Photo locations to render as thumbnail markers on the map. */
  photoLocations?: PhotoLocation[];
}

/**
 * Read-only MapLibre map showing a report's location pin and optional
 * photo location markers. Used on the public report tracking page.
 *
 * Unlike `MapPinPicker`, the pin is not draggable and the map does not
 * accept user input for location changes.
 */
export function ReportLocationMap({ latitude, longitude, photoLocations = [] }: ReportLocationMapProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<MaplibreMap | null>(null);
  const markerRef = useRef<MaplibreMarker | null>(null);
  const [mapError, setMapError] = useState(false);
  const [mapReady, setMapReady] = useState(false);

  // Render photo thumbnail markers on the map.
  usePhotoMarkers(mapReady ? mapRef.current : null, photoLocations);

  // Initialize map once.
  useEffect(() => {
    if (!containerRef.current || mapRef.current) return;

    let map: MaplibreMap;
    try {
      map = createOsmMap(containerRef.current, {
        center: [longitude, latitude],
        zoom: 15,
      });
    } catch {
      setMapError(true);
      return;
    }

    map.on('load', () => {
      const marker = new MaplibreMarker({ color: '#dc2626' })
        .setLngLat([longitude, latitude])
        .addTo(map);

      markerRef.current = marker;
      setMapReady(true);
    });

    mapRef.current = map;

    const ro = new ResizeObserver(() => resizeOsmMap(map));
    ro.observe(containerRef.current);

    return () => {
      ro.disconnect();
      map.remove();
      mapRef.current = null;
      markerRef.current = null;
      setMapReady(false);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Update marker position when coordinates change externally.
  useEffect(() => {
    if (markerRef.current) {
      markerRef.current.setLngLat([longitude, latitude]);
      mapRef.current?.easeTo({ center: [longitude, latitude], duration: 500 });
    }
  }, [latitude, longitude]);

  if (mapError) {
    return (
      <div className="flex h-full min-h-[220px] flex-col items-center justify-center gap-2 rounded-lg border border-border bg-muted/50 p-4 text-center">
        <AlertCircleIcon className="size-6 text-muted-foreground" />
        <p className="text-xs text-muted-foreground">Map unavailable</p>
      </div>
    );
  }

  return (
    <div className="relative h-56 w-full overflow-hidden rounded-lg border border-border">
      <div ref={containerRef} className="h-full w-full" />
      <div className="pointer-events-none absolute bottom-2 left-2 flex items-center gap-1 rounded-md bg-background/80 px-2 py-1 text-xs text-muted-foreground backdrop-blur">
        <MapPinIcon className="h-3 w-3" />
        {latitude.toFixed(5)}, {longitude.toFixed(5)}
      </div>
    </div>
  );
}
